import React, { useState } from "react";
//#region types
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { FormFieldProps, SelectionExtraProps } from "models/propTypes/genericFormProps";
//#endregion
//#region components
import SelectionGroup from "../../selection/selectionGroup";
import SearchSelection from "../../selection/searchSelection";
import ReadOnlyField from "../baseFields/readOnlyField";
import cssExports from "theme/styles/main.module.scss";
import { useFormikContext } from "formik";
//#endregion

function SelectionFormField<T extends Record<string, any>>(props: FormFieldProps<T>) {
  const { name, field, value, handleOnChangeCustom, readonly, disabled } = props;

  React.useImperativeHandle(props.fieldRef, () => ({
    change: (selectedItem?: SelectionItem) => {
      onChangeHandler(selectedItem);
    },
  }));

  const extraProps = field.extraProps as SelectionExtraProps<T>;
  const [selectedItem, setSelectedItem] = useState<SelectionItem | undefined>(
    // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
    extraProps.items?.find((x) => x.id == value)
  );

  const { values } = useFormikContext<T>();

  React.useEffect(() => {
    // eslint-disable-next-line eqeqeq --- disabling to avoid regression testing if changed
    let selectedItem: SelectionItem | undefined = extraProps.items?.find((x) => x.id == value);

    if (selectedItem === undefined) {
      selectedItem =
        value !== undefined
          ? {
              id: value,
              value: extraProps.referenceFieldName ? values[extraProps.referenceFieldName] : "",
            }
          : undefined;
    }

    setSelectedItem(selectedItem);
  }, [field.extraProps, value]);

  const onChangeHandler = (selectedItem?: SelectionItem) => {
    setSelectedItem(selectedItem);

    handleOnChangeCustom(
      name,
      selectedItem?.id as number,
      true,
      extraProps.referenceFieldName as string | undefined,
      (selectedItem?.value as string) ?? undefined
    );
  };

  const selectionField = extraProps.searchFunc ? (
    <SearchSelection
      title={props.field.label}
      items={extraProps.items}
      value={selectedItem}
      onChange={onChangeHandler}
      searchFunc={extraProps.searchFunc}
      selectionDialogTitle={extraProps.selectionDialogTitle ?? ""}
      selectionDialogSubTitle={extraProps.selectionDialogSubTitle}
      disabled={disabled}
      isReadOnly={readonly}
      className={field.classes?.input}
      hideOthersOnSelection={extraProps.hideOthersOnSelection}
    />
  ) : (
    <SelectionGroup
      title={props.field.label}
      items={extraProps.items}
      value={selectedItem ? [selectedItem] : []}
      onChange={(item) => onChangeHandler(item[0])}
      disabled={disabled}
      isReadOnly={readonly}
      className={field.classes?.input}
      hideOthersOnSelection={extraProps.hideOthersOnSelection}
      hideViewAll
      disabledItems={extraProps.disabledItems}
    />
  );

  return readonly ? (
    <ReadOnlyField label={field.label} href={field.href} className={cssExports.readOnlyTextField}>
      {selectionField}
    </ReadOnlyField>
  ) : (
    selectionField
  );
}

export default SelectionFormField;
